// apps/roleforge-app/src/components/chat/ChatTranscript.tsx
// Plan 3b-ii — Phase 1.3: Auto-scrolling list of MessageBubbles.
// Layout rework: also renders inline activity rows after the last message so
// "Searching skill catalog…" surfaces inline with the conversation rather
// than in a separate panel.
//
// Issue #1219 — the transcript was a plain <div>. New replies arrived
// silently for anyone using a screen reader, and nothing named the speaker.
// It is now a labelled `role="log"` live region that announces additions,
// and every bubble carries a speaker name.
//
// Streaming is deliberately kept OUT of the live region: announcing every
// token delta would be unusable. Instead a single polite status line says
// "RoleForge is replying…" while the answer streams, and the finished
// message is announced once when it lands in the transcript.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '../../lib/api-client';
import { MessageBubble, AGENT_SPEAKER_NAME } from './MessageBubble';
import { InlineActivityIndicator } from './InlineActivityIndicator';
import type { ActivityItem } from '../../lib/use-activity-stream';

/**
 * How close to the bottom still counts as "following the conversation".
 * Generous enough to survive sub-pixel rounding and a partially rendered
 * bubble, tight enough that a deliberate scroll up releases the pin.
 */
export const AT_BOTTOM_THRESHOLD_PX = 64;

interface Props {
  messages: Message[];
  /** If set, a streaming agent bubble is appended below the transcript. */
  streamingAgentText?: string;
  /**
   * Plan 3e-ii Task 7.2: forwarded to each MessageBubble so agent messages
   * carrying the `[INVITE_AFFORDANCE]` token can render the inline invite.
   */
  auditId?: string;
  /**
   * Activity items rendered inline after the last message. The router gates
   * delivery so successive items honour min-display-duration.
   */
  activityItems?: ActivityItem[];
  /** Overrides the name shown on the agent's messages. */
  agentSpeakerName?: string;
  /** Overrides the name shown on the reader's own messages. */
  userSpeakerName?: string;
  /** Workspace-default agent avatar, forwarded to agent bubbles. */
  agentAvatarUrl?: string | null;
}

export function ChatTranscript({
  messages,
  streamingAgentText,
  auditId,
  activityItems,
  agentSpeakerName,
  userSpeakerName,
  agentAvatarUrl,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isStreaming = streamingAgentText !== undefined && streamingAgentText !== '';
  const agentName = agentSpeakerName ?? AGENT_SPEAKER_NAME;

  // Issue #1326 (3) — this used to be an unconditional
  // `scrollIntoView({behavior:'smooth'})` keyed on `streamingAgentText`, so a
  // long streamed answer fired hundreds of smooth-scroll animations and yanked
  // the viewport back to the bottom every few milliseconds. Scrolling up to
  // re-read what you already told the agent — the whole reason a multi-hour
  // interview has a transcript — was impossible while it replied.
  //
  // Auto-scroll now only follows the stream when the reader is already at (or
  // near) the bottom. Once they scroll away we leave the viewport alone and
  // offer an explicit "Jump to latest".
  const [pinnedToBottom, setPinnedToBottom] = useState(true);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setPinnedToBottom(distanceFromBottom <= AT_BOTTOM_THRESHOLD_PX);
  }, []);

  // Auto-scroll to bottom when messages, streaming text, or activities change.
  useEffect(() => {
    if (!pinnedToBottom) return;
    // `auto` rather than `smooth`: these are delta-driven and a queued smooth
    // animation per token is what made the transcript unreadable.
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages.length, streamingAgentText, activityItems?.length, pinnedToBottom]);

  const jumpToLatest = useCallback(() => {
    setPinnedToBottom(true);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-label="Conversation"
      className="flex flex-col gap-3 overflow-y-auto"
    >
      {messages.map((msg) => (
        <MessageBubble
          key={msg.id}
          role={msg.role}
          content={msg.content}
          toolName={msg.tool_name ?? undefined}
          auditId={auditId}
          speakerName={msg.role === 'user' ? userSpeakerName : agentSpeakerName}
          avatarUrl={msg.role === 'agent' ? agentAvatarUrl : undefined}
        />
      ))}
      {isStreaming && (
        <>
          {/* The partial answer is visible but not announced token-by-token. */}
          <div aria-hidden="true">
            <MessageBubble
              role="agent"
              content={streamingAgentText}
              isStreaming
              auditId={auditId}
              speakerName={agentSpeakerName}
              avatarUrl={agentAvatarUrl}
            />
          </div>
          <p className="sr-only" role="status">{`${agentName} is replying…`}</p>
        </>
      )}
      {activityItems && activityItems.length > 0 && (
        <div className="flex flex-col gap-1">
          {activityItems.map((item) => (
            <InlineActivityIndicator key={item.id} item={item} />
          ))}
        </div>
      )}
      <div ref={bottomRef} />
      {!pinnedToBottom && (
        <div className="sticky bottom-2 z-10 flex justify-center">
          <button
            type="button"
            onClick={jumpToLatest}
            data-testid="jump-to-latest"
            className="rounded-full border border-line bg-card px-4 py-2 text-xs font-semibold text-ink shadow-soft transition hover:bg-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
          >
            Jump to latest
          </button>
        </div>
      )}
    </div>
  );
}
